import { apiAuthFetcher, mapFetcherResult } from 'api/goodtrust/api'
import useSWR from 'swr'
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite'
import { encodeQuery, FetchResponse } from 'utils/fetcher'
import { keysOf } from 'utils/general'
import { ApiType } from 'utils/types'

export function useUserSiteList(
  body?: ApiType['UserSiteSpecification'],
  config?: SWRInfiniteConfiguration<
    FetchResponse<ApiType['PaginatedResponseUserSiteResponse']>,
    Error
  >
) {
  // if null is returned, the request of the page won't start
  return useSWRInfinite<FetchResponse<ApiType['PaginatedResponseUserSiteResponse']>>(
    (_, prevPageData) => {
      if (
        prevPageData &&
        prevPageData?.json?.current_page != null &&
        prevPageData?.json?.pages_total != null &&
        prevPageData?.json?.current_page >= prevPageData?.json?.pages_total
      ) {
        return null
      }

      return encodeQuery('/api/v1/user/site', {
        ...(body ?? {}),
        page: (prevPageData?.json?.current_page ?? -1) + 1,
      })
    },
    apiAuthFetcher,
    config
  )
}

export function useWillOwnerSites(willUuid?: string) {
  return useSWR<FetchResponse<ApiType['UserDirectiveWillOwnerSiteResponse'][]>>(
    `/api/v1/user/directive/will/${willUuid}/owner-sites`,
    apiAuthFetcher
  )
}

export function useUserSiteSharedWithList() {
  return useSWRInfinite<FetchResponse<ApiType['PaginatedResponseUserSiteResponse']>>(
    (_, prevPageData) => {
      if (
        prevPageData &&
        prevPageData?.json?.current_page != null &&
        prevPageData?.json?.pages_total != null &&
        prevPageData?.json?.current_page >= prevPageData?.json?.pages_total
      ) {
        return null
      }

      return encodeQuery('/api/v1/user/site/sharedWithMe', {
        page: (prevPageData?.json?.current_page ?? -1) + 1,
      })
    },
    apiAuthFetcher
  )
}

export function getUserSite(siteUuid: string) {
  return apiAuthFetcher<ApiType['UserSiteResponse']>(`/api/v1/user/site/${siteUuid}`)
}

export function useUserSite(siteUuid: string) {
  return useSWR<FetchResponse<ApiType['UserSiteResponse']>>(
    siteUuid ? `/api/v1/user/site/${siteUuid}` : null,
    apiAuthFetcher
  )
}

export function postUserSite(body: ApiType['UserSiteRequest']) {
  return apiAuthFetcher<ApiType['UserSiteResponse']>(`/api/v1/user/site`, {
    method: 'POST',
    body,
  })
}

export function putUserSite(siteUuid: string, body: ApiType['UserSiteRequest']) {
  return apiAuthFetcher<ApiType['UserSiteResponse']>(`/api/v1/user/site/${siteUuid}`, {
    method: 'PUT',
    body,
  })
}

export function putUserSiteContact(
  siteUuid: string,
  body: ApiType['ItemShareWithTrustedContactRequest']
) {
  return apiAuthFetcher<void>(`/api/v1/user/site/${siteUuid}/trusted-contact`, {
    method: 'PUT',
    body,
  })
}

export function deleteUserSite(siteUuid: string) {
  return apiAuthFetcher<void>(`/api/v1/user/site/${siteUuid}`, {
    method: 'DELETE',
  })
}

export function postUserSiteCustom(body: ApiType['CustomSiteRequest']) {
  return apiAuthFetcher<ApiType['CustomSiteResponse']>('/api/v1/user/site/custom', {
    method: 'POST',
    body,
  })
}

export function isPopularWill(categoryName: string) {
  return [categoryName.match(/popular/i), categoryName.match(/will/i)].every((x) => !!x)
}

export function useUserSiteCategories(
  params?: ApiType['SiteCategorySpecification'] & { includePopularWill?: boolean }
) {
  return useSWR<
    FetchResponse<{
      [category: string]: ApiType['CategorySiteResponse']
    }>
  >(
    encodeQuery('/api/v1/user/site/sites', {
      category: params?.category,
      filterTop: params?.filterTop,
      includePopularWill: params?.includePopularWill,
    }),
    mapFetcherResult<{
      [category: string]: ApiType['CategorySiteResponse']
    }>(apiAuthFetcher, (result) => {
      if (params?.includePopularWill) return
      const popularWillKey = keysOf(result.json ?? {}).find((categoryName) =>
        isPopularWill(String(categoryName))
      )
      if (popularWillKey && result.json) {
        delete result.json[popularWillKey]
      }
    })
  )
}
